import { type HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '@environment';
import { SHEETS_STRINGS, fill } from '@sharedlib/sheets-editor/sheets.strings';

/**
 * AM-38 in the open editor: how Stitchy's change is shown once ALG-APPLY-REMOTE has put it on
 * the grid (§5.5 step 4, §7.6). Kept free of Angular and the engine so the rules are testable alone.
 */

/** A range a revision changed (ALG-DIFF-RANGES). */
export interface ChangedRange {
  readonly sheetId: string;
  readonly range: string;
}

/** `GET …/workbooks/{id}/revisions/{revision}`. */
export interface RevisionChange {
  readonly revision: number;
  readonly actor: 'user' | 'stitchy';
  readonly summary: string | null;
  readonly changedRanges: readonly ChangedRange[];
}

/** §5.5 step 4: Review highlights for 6,000 ms, a 1.5 px dashed `#7C3CBE` outline. */
export const REVIEW_MS = 6_000;
export const REVIEW_STROKE = '#7C3CBE';
export const REVIEW_STROKE_WIDTH = 1.5;

/** How long the toast with Undo and Review stays up: long enough to reach its buttons. */
export const STITCHY_TOAST_MS = 10_000;

/** What the toast says without a summary to show (a revision whose record is gone). */
export const STITCHY_FALLBACK_TOAST = SHEETS_STRINGS['Stitchy changed this workbook.'];

const SUMMARY_MAX = 80;

/** "Stitchy {summary}", the summary truncated to 80 characters with "…". */
export function stitchyToastText(summary: string | null | undefined): string {
  const text = (summary ?? '').trim();
  if (!text) return STITCHY_FALLBACK_TOAST;
  return fill('Stitchy {summary}', { summary: text.length > SUMMARY_MAX ? `${text.slice(0, SUMMARY_MAX - 1)}…` : text });
}

/** What `revision` changed, or null when it cannot be read (the toast then says less). */
export async function getRevisionChange(http: HttpClient, workbookId: string, revision: number): Promise<RevisionChange | null> {
  try {
    return await firstValueFrom(
      http.get<RevisionChange>(`${environment.baseUrl}/api/sheets/workbooks/${workbookId}/revisions/${revision}`).pipe(timeout(10_000)),
    );
  } catch {
    return null;
  }
}
